/**
 * Messages hooks: list + send (the first optimistic mutation surface).
 *
 * Pattern (INTERACTION_SPEC §3):
 *
 *   1. User hits Send.
 *   2. We immediately insert an *optimistic* row into the cache with
 *      `__sendStatus: 'sending'` and a temp `__optimisticId`.
 *   3. The server responds with the canonical row.
 *   4. We replace the optimistic row with the canonical row in the
 *      cache (animated transition: status flips to 'sent').
 *   5. If the request fails, we mark the optimistic row with
 *      `__sendStatus: 'error'` so the UI can show a red border + retry.
 *
 * The retry path re-runs the mutation with the same input and the same
 * `__optimisticId`. We swap the failed row for the new optimistic row.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  messagesApi,
  type SendMessageInput,
} from '@beacon/api-client';
import type { Message } from '@beacon/core';
import { uuidv7 } from '@beacon/core';

import { beaconClient } from '@/lib/api/client.js';

export const messageKeys = {
  all: ['messages'] as const,
  list: (ticketNumber: string) =>
    [...messageKeys.all, 'list', ticketNumber] as const,
};

/** A message in the local cache, possibly an unconfirmed optimistic insert. */
export interface CachedMessage extends Message {
  __sendStatus?: 'sending' | 'sent' | 'error';
  __optimisticId?: string;
  __errorMessage?: string;
}

export function useMessages(ticketNumber: string | undefined) {
  return useQuery({
    queryKey: ticketNumber
      ? messageKeys.list(ticketNumber)
      : messageKeys.all,
    queryFn: async (): Promise<CachedMessage[]> => {
      const res = await messagesApi.list(beaconClient, ticketNumber!);
      return res.messages;
    },
    enabled: Boolean(ticketNumber),
  });
}

interface SendVars {
  ticketNumber: string;
  /** A pre-generated optimistic id — pass when retrying so we replace the failed row. */
  optimisticId?: string;
  input: SendMessageInput;
  files?: ReadonlyArray<File>;
}

export function useSendMessage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketNumber, input, files }: SendVars) =>
      messagesApi.send(beaconClient, ticketNumber, input, files),

    onMutate: async ({ ticketNumber, input, optimisticId, files }) => {
      const key = messageKeys.list(ticketNumber);
      await qc.cancelQueries({ queryKey: key });

      const previous = qc.getQueryData<CachedMessage[]>(key) ?? [];

      const tempId = optimisticId ?? `optimistic-${uuidv7()}`;
      const now = new Date().toISOString();

      // Show pending file metadata as chips immediately so the user
      // sees their attachments while the upload is in-flight. The
      // server replaces these with canonical refs on success.
      const optimisticAttachments: Message['attachments'] = (files ?? [])
        .filter((f) => f.size > 0)
        .map((f, i) => ({
          id: `${tempId}-att-${i}` as Message['attachments'][number]['id'],
          filename: f.name,
          mimeType: f.type || 'application/octet-stream',
          sizeBytes: f.size,
        }));

      const optimistic: CachedMessage = {
        id: tempId as Message['id'],
        ticketId: previous[0]?.ticketId ?? ('' as Message['ticketId']),
        authorUserId: 'user-charlie' as Message['authorUserId'],
        type: input.type,
        bodyHtml: input.bodyHtml,
        bodyText: input.bodyText,
        attachments: optimisticAttachments,
        emailMessageId: null,
        createdAt: now as Message['createdAt'],
        __sendStatus: 'sending',
        __optimisticId: tempId,
      };

      // If retrying, drop the previous failed row with the same __optimisticId.
      const cleaned = previous.filter(
        (m) => m.__optimisticId !== tempId
      );
      qc.setQueryData<CachedMessage[]>(key, [...cleaned, optimistic]);

      return { previous, tempId, ticketNumber };
    },

    onSuccess: (server, _vars, ctx) => {
      if (!ctx) return;
      const key = messageKeys.list(ctx.ticketNumber);
      qc.setQueryData<CachedMessage[]>(key, (current) => {
        const list = current ?? [];
        return list.map((m) =>
          m.__optimisticId === ctx.tempId
            ? { ...server, __sendStatus: 'sent' as const }
            : m
        );
      });
    },

    onError: (err, _vars, ctx) => {
      if (!ctx) return;
      const key = messageKeys.list(ctx.ticketNumber);
      qc.setQueryData<CachedMessage[]>(key, (current) => {
        const list = current ?? [];
        return list.map((m) =>
          m.__optimisticId === ctx.tempId
            ? {
                ...m,
                __sendStatus: 'error' as const,
                __errorMessage:
                  err instanceof Error ? err.message : 'Send failed',
              }
            : m
        );
      });
    },

    // Reconcile against the server after a short delay — guards
    // against any drift if other clients also wrote.
    onSettled: (_data, _err, vars) => {
      const key = messageKeys.list(vars.ticketNumber);
      // Don't immediately invalidate — let the optimistic→sent
      // transition animate first.
      setTimeout(() => qc.invalidateQueries({ queryKey: key }), 800);
    },
  });
}
